import React, { useMemo } from 'react';
import { LFC } from '../../Legacy';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { CommandDispatcher } from '../../concepts/commands/CommandDispatcher';
import { useLoader, useSubmitter } from '../../platform/Hook';
import { BigButton } from '../../ui/BigButton';
import { LoadingGuard } from '../../ui/LoadingGuard';
import { Notice } from '../../ui/Notice';
import { ResultInfoForSubmitter } from '../../ui/ResultInfo';
import { useResolve } from '../Container';
import { PassportModel, UserRole } from '../PassportModel';
import { useDashboardVersion } from '../account/AccountHooks';
import { HideIfHasFeature, useFeatureCheck } from '../common/FeatureElements';
import { Copy, Flexer, Title } from '../dashboard/elements/LayoutElements';
import { Popup, PopupProps } from '../popups/Popup';
import { Prompt } from './../../ui';
import { userChangeRole } from './ManageUsersCommands';

export const manageUserRolePopup = (userId: string) => ({ ui: ManageUserRolePopup, args: { userId } });

export const ManageUserRolePopup: LFC<PopupProps> = ({ action, dismiss, args }) => {
    const { userId } = args;

    const api = useResolve(ApiClient);
    const dispatch = useResolve(CommandDispatcher);
    const passport = useResolve(PassportModel);
    const accessControl = useFeatureCheck('accessControl');
    const version = useDashboardVersion();

    const [loading, info, err] = useLoader(
        async () => await api.exec(v2.admin.users.info(userId)),
        [userId]
    );

    const [onSubmit, working, result] = useSubmitter(async (roleName: string) => {
        await dispatch(userChangeRole, [userId, roleName]);
        action(true);
    }, [userId]);

    const options = useMemo(
        () => UserRole.options(passport, info?.roleName).filter(x => version !== 'next' || x.name !== 'account_guest'),
        [info, version]);

    return <Prompt ignoreEnter onAction={dismiss}>
        <Popup w={38}>
            <Popup.Close onAction={dismiss} />
            <Popup.Title>Change Role</Popup.Title>
            <Popup.Content>
                <LoadingGuard loading={loading}>
                    <Copy>
                        Please select a new role for this person:
                    </Copy>
                    {options.map(x =>
                        <Flexer key={x.name} margin=".5 0" disabled={working || (x.name === 'account_guest' && !accessControl)}>
                            <BigButton onClick={() => onSubmit(x.name)}>
                                <BigButton.Inner expand>
                                    <Title size="md">{x.label}</Title>
                                    <Copy>{x.desc}</Copy>
                                </BigButton.Inner>
                            </BigButton>
                        </Flexer>
                    )}
                </LoadingGuard>
                <HideIfHasFeature name="accessControl">
                    <Flexer flex="row" center margin=".5 0 0">
                        <Notice icon="upgrade" text="Upgrade to get guests - invite external users to your boards, no sign-in required." />
                    </Flexer>
                </HideIfHasFeature>
                <ResultInfoForSubmitter result={result} />
            </Popup.Content>
            <Popup.Actions>
                {/* <Button disabled={working} variant="primary" onAction={onGenerate}>
                    {working ? 'Generating...' : 'Generate'}
                    {working && <Loader
                        type="TailSpin"
                        color="#fff"
                        height={remInPx(1.2)}
                        width={remInPx(1.2)}
                    />}
                </Button>
                <Button disabled={working} onAction={dismiss}>
                    Cancel
                </Button> */}
            </Popup.Actions>
        </Popup>
    </Prompt>;
};


